import { useAppState, useAppDispatch } from '../../Shared/AppContext';
import { emptyExperienceDocumentManager, documentManagerFromExperience } from './experienceDocumentManager';

export function useProfileState() {
  return useAppState();
}

export function useProfileActions() {
  const dispatch = useAppDispatch();

  function initializeState(userId) {
    dispatch((appState) => ({
      ...appState,
      loading: true,
      user: {
        id: userId,
      },
      profilePictureUpdateModalIsOpen: false,
      userNameUpdateModalIsOpen: false,
      userBiographyUpdateModalIsOpen: false,
      userSkillsModalIsOpen: false,
      newUserExperienceModalIsOpen: false,
      updateUserExperienceModalIsOpen: false,
      deleteUserExperienceModalIsOpen: false,
      newUserEducationModalIsOpen: false,
      updateUserEducationModalIsOpen: false,
      deleteUserEducationModalIsOpen: false,
      newUserCertificationModalIsOpen: false,
      updateUserCertificationModalIsOpen: false,
      deleteUserCertificationModalIsOpen: false,
      userInterestsModalIsOpen: false,
      userProfilePictureForm: {
        profilePictureBase64String: null,
        profilePictureFileName: null,
      },
      userNameUpdateForm: {
        firstName: '',
        lastName: '',
      },
      userBiographyUpdateForm: {
        biography: '',
      },
      skillsModal: {
        loading: true,
        pageNumber: 1,
        numberOfPages: 1,
        skills: [],
        newUnrecognizedSkillForm: {
          skillName: '',
          debouncedSkillNameForFuzzyFilter: '',
        },
      },
      newUserExperienceForm: {
        title: '',
        biopharmaGroupName: '',
        startYear: '',
        startMonth: '',
        endYear: '',
        endMonth: '',
      },
      updateUserExperienceForm: {
        experienceId: '',
        title: '',
        biopharmaGroupName: '',
        startYear: '',
        startMonth: '',
        endYear: '',
        endMonth: '',
      },
      deleteUserExperienceForm: {
        experienceId: '',
      },
      newUserExperienceEditor: null,
      updateUserExperienceEditor: null,
      newUserEducationForm: {
        institution: '',
        degreeType: '',
        fieldOfStudy: '',
        graduationYear: '',
        description: '',
      },
      updateUserEducationForm: {
        educationId: '',
        institution: '',
        degreeType: '',
        fieldOfStudy: '',
        graduationYear: '',
        description: '',
      },
      deleteUserEducationForm: {
        educationId: '',
      },
      newUserCertificationForm: {
        name: '',
        issuingOrganization: '',
        dateObtained: '',
        expirationDate: '',
        credentialId: '',
      },
      updateUserCertificationForm: {
        certificationId: '',
        name: '',
        issuingOrganization: '',
        dateObtained: '',
        expirationDate: '',
        credentialId: '',
      },
      deleteUserCertificationForm: {
        certificationId: '',
      },
      interestsModal: {
        loading: true,
        pageNumber: 1,
        numberOfPages: 1,
        interests: [],
        newUnrecognizedInterestForm: {
          interestName: '',
          debouncedInterestNameForFuzzyFilter: '',
        },
      },
      feedbackMessage: null,
      feedbackVariant: null,
    }));
  }

  function onResponse(response) {
    dispatch((appState) => ({ ...appState, ...response }));
  }

  function onError(feedback) {
    dispatch((appState) => ({ ...appState, ...feedback }));
  }

  function openProfilePictureUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      profilePictureUpdateModalIsOpen: true,
      userProfilePictureForm: {
        profilePictureBase64String: appState.user?.profilePictureBase64String || null,
        profilePictureFileName: appState.user?.profilePictureFileName || null,
        isDefaultProfilePicture: appState.user?.isDefaultProfilePicture || false,
        profilePicturePath: appState.user?.profilePicturePath || null,
      },
    }));
  }

  function closeProfilePictureUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      profilePictureUpdateModalIsOpen: false,
      userProfilePictureForm: {
        profilePictureBase64String: null,
        profilePictureFileName: null,
      },
    }));
  }

  function openUserNameUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      userNameUpdateModalIsOpen: true,
      userNameUpdateForm: {
        firstName: appState.user?.firstName || '',
        lastName: appState.user?.lastName || '',
      },
    }));
  }

  function closeUserNameUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      userNameUpdateModalIsOpen: false,
      userNameUpdateForm: {
        firstName: '',
        lastName: '',
      },
    }));
  }

  function openUserBiographyUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      userBiographyUpdateModalIsOpen: true,
      userBiographyUpdateForm: {
        biography: appState.user?.biography || '',
      },
    }));
  }

  function closeUserBiographyUpdateModal() {
    dispatch((appState) => ({
      ...appState,
      userBiographyUpdateModalIsOpen: false,
      userBiographyUpdateForm: {
        biography: '',
      },
    }));
  }

  function openUserSkillsModal() {
    dispatch((appState) => ({
      ...appState,
      userSkillsModalIsOpen: true,
    }));
  }

  function closeUserSkillsModal() {
    dispatch((appState) => ({
      ...appState,
      userSkillsModalIsOpen: false,
    }));
  }

  function setSkillsModalToLoading() {
    dispatch((appState) => ({
      ...appState,
      skillsModal: {
        ...appState.skillsModal,
        loading: true,
      },
    }));
  }

  function updateNewSkillName(skillName) {
    dispatch((appState) => ({
      ...appState,
      skillsModal: {
        ...appState.skillsModal,
        newUnrecognizedSkillForm: {
          ...appState.skillsModal.newUnrecognizedSkillForm,
          skillName: skillName,
        }
      },
    }));
  }

  function updateDebouncedSkillNameForFuzzyFilter(debouncedSkillNameForFuzzyFilter) {
    dispatch((appState) => ({
      ...appState,
      skillsModal: {
        ...appState.skillsModal,
        newUnrecognizedSkillForm: {
          ...appState.skillsModal.newUnrecognizedSkillForm,
          debouncedSkillNameForFuzzyFilter: debouncedSkillNameForFuzzyFilter,
        }
      },
    }));
  }

  function openNewUserExperienceModal() {
    dispatch((appState) => ({
      ...appState,
      newUserExperienceModalIsOpen: true,
      newUserExperienceForm: {
        title: '',
        biopharmaGroupName: '',
        startYear: '',
        startMonth: '',
        endYear: '',
        endMonth: '',
      },
      newUserExperienceEditor: {
        documentManager: emptyExperienceDocumentManager(),
      },
    }));
  }

  function closeNewUserExperienceModal() {
    dispatch((appState) => ({
      ...appState,
      newUserExperienceModalIsOpen: false,
      newUserExperienceForm: {
        title: '',
        biopharmaGroupName: '',
        startYear: '',
        startMonth: '',
        endYear: '',
        endMonth: '',
      },
      newUserExperienceEditor: null,
    }));
  }

  function openUpdateUserExperienceModal(experience) {
    dispatch((appState) => ({
      ...appState,
      updateUserExperienceModalIsOpen: true,
      updateUserExperienceForm: {
        experienceId: experience.id,
        title: experience.title,
        biopharmaGroupName: experience.biopharmaGroupName,
        startYear: experience.startYear,
        startMonth: experience.startMonth,
        endYear: experience.endYear,
        endMonth: experience.endMonth,
      },
      updateUserExperienceEditor: {
        documentManager: documentManagerFromExperience(experience),
      },
    }));
  }

  function closeUpdateUserExperienceModal() {
    dispatch((appState) => ({
      ...appState,
      updateUserExperienceModalIsOpen: false,
      updateUserExperienceForm: {
        experienceId: '',
        title: '',
        biopharmaGroupName: '',
        startYear: '',
        startMonth: '',
        endYear: '',
        endMonth: '',
      },
      updateUserExperienceEditor: null,
    }));
  }

  function openDeleteUserExperienceModal(experience) {
    dispatch((appState) => ({
      ...appState,
      deleteUserExperienceModalIsOpen: true,
      deleteUserExperienceForm: {
        experienceId: experience.id,
      },
    }));
  }

  function closeDeleteUserExperienceModal() {
    dispatch((appState) => ({
      ...appState,
      deleteUserExperienceModalIsOpen: false,
      deleteUserExperienceForm: {
        experienceId: '',
      },
    }));
  }

  function openNewUserEducationModal() {
    dispatch((appState) => ({
      ...appState,
      newUserEducationModalIsOpen: true,
      newUserEducationForm: {
        institution: '',
        degreeType: '',
        fieldOfStudy: '',
        graduationYear: '',
        description: '',
      },
    }));
  }

  function closeNewUserEducationModal() {
    dispatch((appState) => ({
      ...appState,
      newUserEducationModalIsOpen: false,
      newUserEducationForm: {
        institution: '',
        degreeType: '',
        fieldOfStudy: '',
        graduationYear: '',
        description: '',
      },
    }));
  }

  function openUpdateUserEducationModal(education) {
    dispatch((appState) => ({
      ...appState,
      updateUserEducationModalIsOpen: true,
      updateUserEducationForm: {
        educationId: education.id,
        institution: education.institution,
        degreeType: education.degreeType,
        fieldOfStudy: education.fieldOfStudy,
        graduationYear: education.graduationYear ?? '',
        description: education.description,
      },
    }));
  }

  function closeUpdateUserEducationModal() {
    dispatch((appState) => ({
      ...appState,
      updateUserEducationModalIsOpen: false,
      updateUserEducationForm: {
        educationId: '',
        institution: '',
        degreeType: '',
        fieldOfStudy: '',
        graduationYear: '',
        description: '',
      },
    }));
  }

  function openDeleteUserEducationModal(education) {
    dispatch((appState) => ({
      ...appState,
      deleteUserEducationModalIsOpen: true,
      deleteUserEducationForm: {
        educationId: education.id,
      },
    }));
  }

  function closeDeleteUserEducationModal() {
    dispatch((appState) => ({
      ...appState,
      deleteUserEducationModalIsOpen: false,
      deleteUserEducationForm: {
        educationId: '',
      },
    }));
  }

  function openNewUserCertificationModal() {
    dispatch((appState) => ({
      ...appState,
      newUserCertificationModalIsOpen: true,
      newUserCertificationForm: {
        name: '',
        issuingOrganization: '',
        dateObtained: '',
        expirationDate: '',
        credentialId: '',
      },
    }));
  }

  function closeNewUserCertificationModal() {
    dispatch((appState) => ({
      ...appState,
      newUserCertificationModalIsOpen: false,
      newUserCertificationForm: {
        name: '',
        issuingOrganization: '',
        dateObtained: '',
        expirationDate: '',
        credentialId: '',
      },
    }));
  }

  function openUpdateUserCertificationModal(certification) {
    dispatch((appState) => ({
      ...appState,
      updateUserCertificationModalIsOpen: true,
      updateUserCertificationForm: {
        certificationId: certification.id,
        name: certification.name,
        issuingOrganization: certification.issuingOrganization ?? '',
        dateObtained: certification.dateObtained ?? '',
        expirationDate: certification.expirationDate ?? '',
        credentialId: certification.credentialId ?? '',
      },
    }));
  }

  function closeUpdateUserCertificationModal() {
    dispatch((appState) => ({
      ...appState,
      updateUserCertificationModalIsOpen: false,
      updateUserCertificationForm: {
        certificationId: '',
        name: '',
        issuingOrganization: '',
        dateObtained: '',
        expirationDate: '',
        credentialId: '',
      },
    }));
  }

  function openDeleteUserCertificationModal(certification) {
    dispatch((appState) => ({
      ...appState,
      deleteUserCertificationModalIsOpen: true,
      deleteUserCertificationForm: {
        certificationId: certification.id,
      },
    }));
  }

  function closeDeleteUserCertificationModal() {
    dispatch((appState) => ({
      ...appState,
      deleteUserCertificationModalIsOpen: false,
      deleteUserCertificationForm: {
        certificationId: '',
      },
    }));
  }

  function openUserInterestsModal() {
    dispatch((appState) => ({
      ...appState,
      userInterestsModalIsOpen: true,
    }));
  }

  function closeUserInterestsModal() {
    dispatch((appState) => ({
      ...appState,
      userInterestsModalIsOpen: false,
    }));
  }

  function setInterestsModalToLoading() {
    dispatch((appState) => ({
      ...appState,
      interestsModal: {
        ...appState.interestsModal,
        loading: true,
      },
    }));
  }

  function updateNewInterestName(interestName) {
    dispatch((appState) => ({
      ...appState,
      interestsModal: {
        ...appState.interestsModal,
        newUnrecognizedInterestForm: {
          ...appState.interestsModal.newUnrecognizedInterestForm,
          interestName: interestName,
        }
      },
    }));
  }

  function updateDebouncedInterestNameForFuzzyFilter(debouncedInterestNameForFuzzyFilter) {
    dispatch((appState) => ({
      ...appState,
      interestsModal: {
        ...appState.interestsModal,
        newUnrecognizedInterestForm: {
          ...appState.interestsModal.newUnrecognizedInterestForm,
          debouncedInterestNameForFuzzyFilter: debouncedInterestNameForFuzzyFilter,
        }
      },
    }));
  }

  function updateProfilePictureBase64StringAndFileName(profilePictureBase64String, profilePictureFileName) {
    dispatch((appState) => ({
      ...appState,
      userProfilePictureForm: {
        ...appState.userProfilePictureForm,
        profilePictureBase64String: profilePictureBase64String,
        profilePictureFileName: profilePictureFileName,
      },
    }));
  }

  function updateNewUserFirstName(firstName) {
    dispatch((appState) => ({
      ...appState,
      userNameUpdateForm: {
        ...appState.userNameUpdateForm,
        firstName: firstName,
      },
    }));
  }

  function updateNewUserLastName(lastName) {
    dispatch((appState) => ({
      ...appState,
      userNameUpdateForm: {
        ...appState.userNameUpdateForm,
        lastName: lastName,
      },
    }));
  }

  function updateNewUserBiography(biography) {
    dispatch((appState) => ({
      ...appState,
      userBiographyUpdateForm: {
        ...appState.userBiographyUpdateForm,
        biography: biography,
      },
    }));
  }

  function updateNewUserExperienceFormTitle(title) {
    dispatch((appState) => ({
      ...appState,
      newUserExperienceForm: {
        ...appState.newUserExperienceForm,
        title: title,
      },
    }));
  }

  function updateNewUserExperienceFormBiopharmaGroupName(biopharmaGroupName) {
    dispatch((appState) => ({
      ...appState,
      newUserExperienceForm: {
        ...appState.newUserExperienceForm,
        biopharmaGroupName: biopharmaGroupName,
      },
    }));
  }

  function updateNewUserExperienceFormStartMonth(startMonth) {
    dispatch((appState) => ({
      ...appState,
      newUserExperienceForm: {
        ...appState.newUserExperienceForm,
        startMonth: startMonth,
      },
    }));
  }

  function updateNewUserExperienceFormStartYear(startYear) {
    dispatch((appState) => ({
      ...appState,
      newUserExperienceForm: {
        ...appState.newUserExperienceForm,
        startYear: startYear,
      },
    }));
  }

  function updateNewUserExperienceFormEndMonth(endMonth) {
    dispatch((appState) => ({
      ...appState,
      newUserExperienceForm: {
        ...appState.newUserExperienceForm,
        endMonth: endMonth,
      },
    }));
  }

  function updateNewUserExperienceFormEndYear(endYear) {
    dispatch((appState) => ({
      ...appState,
      newUserExperienceForm: {
        ...appState.newUserExperienceForm,
        endYear: endYear,
      },
    }));
  }

  function updateUserExperienceFormTitle(title) {
    dispatch((appState) => ({
      ...appState,
      updateUserExperienceForm: {
        ...appState.updateUserExperienceForm,
        title: title,
      },
    }));
  }

  function updateUserExperienceFormBiopharmaGroupName(biopharmaGroupName) {
    dispatch((appState) => ({
      ...appState,
      updateUserExperienceForm: {
        ...appState.updateUserExperienceForm,
        biopharmaGroupName: biopharmaGroupName,
      },
    }));
  }

  function updateUserExperienceFormStartMonth(startMonth) {
    dispatch((appState) => ({
      ...appState,
      updateUserExperienceForm: {
        ...appState.updateUserExperienceForm,
        startMonth: startMonth,
      },
    }));
  }

  function updateUserExperienceFormStartYear(startYear) {
    dispatch((appState) => ({
      ...appState,
      updateUserExperienceForm: {
        ...appState.updateUserExperienceForm,
        startYear: startYear,
      },
    }));
  }

  function updateUserExperienceFormEndMonth(endMonth) {
    dispatch((appState) => ({
      ...appState,
      updateUserExperienceForm: {
        ...appState.updateUserExperienceForm,
        endMonth: endMonth,
      },
    }));
  }

  function updateUserExperienceFormEndYear(endYear) {
    dispatch((appState) => ({
      ...appState,
      updateUserExperienceForm: {
        ...appState.updateUserExperienceForm,
        endYear: endYear,
      },
    }));
  }

  function setNewUserExperienceEditorState(updater) {
    dispatch((appState) => {
      const prevEditorState = {
        documentManager: appState.newUserExperienceEditor?.documentManager,
        feedbackMessage: appState.feedbackMessage,
        feedbackVariant: appState.feedbackVariant,
      };
      const nextEditorState = typeof updater === 'function' ? updater(prevEditorState) : updater;
      return {
        ...appState,
        newUserExperienceEditor: {
          ...(appState.newUserExperienceEditor || {}),
          documentManager: nextEditorState.documentManager,
          activeStyleTypes: nextEditorState.activeStyleTypes,
          activeFormat: nextEditorState.activeFormat,
          activeHref: nextEditorState.activeHref,
        },
        feedbackMessage: nextEditorState.feedbackMessage !== undefined ? nextEditorState.feedbackMessage : appState.feedbackMessage,
        feedbackVariant: nextEditorState.feedbackVariant !== undefined ? nextEditorState.feedbackVariant : appState.feedbackVariant,
      };
    });
  }

  function setUpdateUserExperienceEditorState(updater) {
    dispatch((appState) => {
      const prevEditorState = {
        documentManager: appState.updateUserExperienceEditor?.documentManager,
        feedbackMessage: appState.feedbackMessage,
        feedbackVariant: appState.feedbackVariant,
      };
      const nextEditorState = typeof updater === 'function' ? updater(prevEditorState) : updater;
      return {
        ...appState,
        updateUserExperienceEditor: {
          ...(appState.updateUserExperienceEditor || {}),
          documentManager: nextEditorState.documentManager,
          activeStyleTypes: nextEditorState.activeStyleTypes,
          activeFormat: nextEditorState.activeFormat,
          activeHref: nextEditorState.activeHref,
        },
        feedbackMessage: nextEditorState.feedbackMessage !== undefined ? nextEditorState.feedbackMessage : appState.feedbackMessage,
        feedbackVariant: nextEditorState.feedbackVariant !== undefined ? nextEditorState.feedbackVariant : appState.feedbackVariant,
      };
    });
  }

  function updateNewUserEducationFormInstitution(institution) {
    dispatch((appState) => ({
      ...appState,
      newUserEducationForm: {
        ...appState.newUserEducationForm,
        institution: institution,
      },
    }));
  }

  function updateNewUserEducationFormDegreeType(degreeType) {
    dispatch((appState) => ({
      ...appState,
      newUserEducationForm: {
        ...appState.newUserEducationForm,
        degreeType: degreeType,
      },
    }));
  }

  function updateNewUserEducationFormFieldOfStudy(fieldOfStudy) {
    dispatch((appState) => ({
      ...appState,
      newUserEducationForm: {
        ...appState.newUserEducationForm,
        fieldOfStudy: fieldOfStudy,
      },
    }));
  }

  function updateNewUserEducationFormGraduationYear(graduationYear) {
    dispatch((appState) => ({
      ...appState,
      newUserEducationForm: {
        ...appState.newUserEducationForm,
        graduationYear: graduationYear,
      },
    }));
  }

  function updateNewUserEducationFormDescription(description) {
    dispatch((appState) => ({
      ...appState,
      newUserEducationForm: {
        ...appState.newUserEducationForm,
        description: description,
      },
    }));
  }

  function updateUserEducationFormInstitution(institution) {
    dispatch((appState) => ({
      ...appState,
      updateUserEducationForm: {
        ...appState.updateUserEducationForm,
        institution: institution,
      },
    }));
  }

  function updateUserEducationFormDegreeType(degreeType) {
    dispatch((appState) => ({
      ...appState,
      updateUserEducationForm: {
        ...appState.updateUserEducationForm,
        degreeType: degreeType,
      },
    }));
  }

  function updateUserEducationFormFieldOfStudy(fieldOfStudy) {
    dispatch((appState) => ({
      ...appState,
      updateUserEducationForm: {
        ...appState.updateUserEducationForm,
        fieldOfStudy: fieldOfStudy,
      },
    }));
  }

  function updateUserEducationFormGraduationYear(graduationYear) {
    dispatch((appState) => ({
      ...appState,
      updateUserEducationForm: {
        ...appState.updateUserEducationForm,
        graduationYear: graduationYear,
      },
    }));
  }

  function updateUserEducationFormDescription(description) {
    dispatch((appState) => ({
      ...appState,
      updateUserEducationForm: {
        ...appState.updateUserEducationForm,
        description: description,
      },
    }));
  }

  function updateNewUserCertificationFormName(name) {
    dispatch((appState) => ({
      ...appState,
      newUserCertificationForm: {
        ...appState.newUserCertificationForm,
        name: name,
      },
    }));
  }

  function updateNewUserCertificationFormIssuingOrganization(issuingOrganization) {
    dispatch((appState) => ({
      ...appState,
      newUserCertificationForm: {
        ...appState.newUserCertificationForm,
        issuingOrganization: issuingOrganization,
      },
    }));
  }

  function updateNewUserCertificationFormDateObtained(dateObtained) {
    dispatch((appState) => ({
      ...appState,
      newUserCertificationForm: {
        ...appState.newUserCertificationForm,
        dateObtained: dateObtained,
      },
    }));
  }

  function updateNewUserCertificationFormExpirationDate(expirationDate) {
    dispatch((appState) => ({
      ...appState,
      newUserCertificationForm: {
        ...appState.newUserCertificationForm,
        expirationDate: expirationDate,
      },
    }));
  }

  function updateNewUserCertificationFormCredentialId(credentialId) {
    dispatch((appState) => ({
      ...appState,
      newUserCertificationForm: {
        ...appState.newUserCertificationForm,
        credentialId: credentialId,
      },
    }));
  }

  function updateUserCertificationFormName(name) {
    dispatch((appState) => ({
      ...appState,
      updateUserCertificationForm: {
        ...appState.updateUserCertificationForm,
        name: name,
      },
    }));
  }

  function updateUserCertificationFormIssuingOrganization(issuingOrganization) {
    dispatch((appState) => ({
      ...appState,
      updateUserCertificationForm: {
        ...appState.updateUserCertificationForm,
        issuingOrganization: issuingOrganization,
      },
    }));
  }

  function updateUserCertificationFormDateObtained(dateObtained) {
    dispatch((appState) => ({
      ...appState,
      updateUserCertificationForm: {
        ...appState.updateUserCertificationForm,
        dateObtained: dateObtained,
      },
    }));
  }

  function updateUserCertificationFormExpirationDate(expirationDate) {
    dispatch((appState) => ({
      ...appState,
      updateUserCertificationForm: {
        ...appState.updateUserCertificationForm,
        expirationDate: expirationDate,
      },
    }));
  }

  function updateUserCertificationFormCredentialId(credentialId) {
    dispatch((appState) => ({
      ...appState,
      updateUserCertificationForm: {
        ...appState.updateUserCertificationForm,
        credentialId: credentialId,
      },
    }));
  }

  return {
    initializeState,
    onResponse,
    onError,
    openProfilePictureUpdateModal,
    closeProfilePictureUpdateModal,
    openUserNameUpdateModal,
    closeUserNameUpdateModal,
    openUserBiographyUpdateModal,
    closeUserBiographyUpdateModal,
    openUserSkillsModal,
    closeUserSkillsModal,
    setSkillsModalToLoading,
    updateNewSkillName,
    updateDebouncedSkillNameForFuzzyFilter,
    openNewUserExperienceModal,
    closeNewUserExperienceModal,
    openUpdateUserExperienceModal,
    closeUpdateUserExperienceModal,
    openDeleteUserExperienceModal,
    closeDeleteUserExperienceModal,
    openNewUserEducationModal,
    closeNewUserEducationModal,
    openUpdateUserEducationModal,
    closeUpdateUserEducationModal,
    openDeleteUserEducationModal,
    closeDeleteUserEducationModal,
    openNewUserCertificationModal,
    closeNewUserCertificationModal,
    openUpdateUserCertificationModal,
    closeUpdateUserCertificationModal,
    openDeleteUserCertificationModal,
    closeDeleteUserCertificationModal,
    openUserInterestsModal,
    closeUserInterestsModal,
    setInterestsModalToLoading,
    updateNewInterestName,
    updateDebouncedInterestNameForFuzzyFilter,
    updateProfilePictureBase64StringAndFileName,
    updateNewUserFirstName,
    updateNewUserLastName,
    updateNewUserBiography,
    updateNewUserExperienceFormTitle,
    updateNewUserExperienceFormBiopharmaGroupName,
    updateNewUserExperienceFormStartMonth,
    updateNewUserExperienceFormStartYear,
    updateNewUserExperienceFormEndMonth,
    updateNewUserExperienceFormEndYear,
    updateUserExperienceFormTitle,
    updateUserExperienceFormBiopharmaGroupName,
    updateUserExperienceFormStartMonth,
    updateUserExperienceFormStartYear,
    updateUserExperienceFormEndMonth,
    updateUserExperienceFormEndYear,
    setNewUserExperienceEditorState,
    setUpdateUserExperienceEditorState,
    updateNewUserEducationFormInstitution,
    updateNewUserEducationFormDegreeType,
    updateNewUserEducationFormFieldOfStudy,
    updateNewUserEducationFormGraduationYear,
    updateNewUserEducationFormDescription,
    updateUserEducationFormInstitution,
    updateUserEducationFormDegreeType,
    updateUserEducationFormFieldOfStudy,
    updateUserEducationFormGraduationYear,
    updateUserEducationFormDescription,
    updateNewUserCertificationFormName,
    updateNewUserCertificationFormIssuingOrganization,
    updateNewUserCertificationFormDateObtained,
    updateNewUserCertificationFormExpirationDate,
    updateNewUserCertificationFormCredentialId,
    updateUserCertificationFormName,
    updateUserCertificationFormIssuingOrganization,
    updateUserCertificationFormDateObtained,
    updateUserCertificationFormExpirationDate,
    updateUserCertificationFormCredentialId,
  };
}
